import type { IDENTITY_POLICY } from "../policy";

/** One-time email codes (27 §5 conventions, FR-ADMIN-003): throttling, expiry and lock-out as pure functions. */

export type CodePolicy = (typeof IDENTITY_POLICY)["code"];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** Splits pasted or typed lists ("a@x.in, b@y.in" or one per line) into normalised, de-duplicated emails. */
export function parseEmailList(text: string): { valid: string[]; invalid: string[] } {
  const parts = text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter((part) => part !== "");
  const valid = [...new Set(parts.filter(isEmail).map(normaliseEmail))];
  const invalid = parts.filter((part) => !isEmail(part));
  return { valid, invalid };
}

export type ThrottleResult = { allowed: true } | { allowed: false; retryAt: number };

/** At most `perWindow` codes per email in a rolling window (FR-ADMIN-003: 10 per hour; the 11th is refused). */
export function throttleCodes(
  sentAt: readonly number[],
  now: number,
  policy: CodePolicy,
): ThrottleResult {
  const recent = sentAt.filter((at) => now - at < policy.windowMs).sort((a, b) => a - b);
  if (recent.length < policy.perWindow) return { allowed: true };
  const oldest = recent[recent.length - policy.perWindow] ?? now;
  return { allowed: false, retryAt: oldest + policy.windowMs };
}

/** Send times still inside the window, so stored history stays small. */
export function pruneSends(sentAt: readonly number[], now: number, policy: CodePolicy): number[] {
  return sentAt.filter((at) => now - at < policy.windowMs);
}

export interface IssuedCode {
  issuedAt: number;
  expiresAt: number;
  attempts: number;
  lockedUntil: number | undefined;
  used: boolean;
}

export function issueCode(now: number, policy: CodePolicy): IssuedCode {
  return {
    issuedAt: now,
    expiresAt: now + policy.validMs,
    attempts: 0,
    lockedUntil: undefined,
    used: false,
  };
}

export type CheckResult =
  | { result: "ok"; code: IssuedCode }
  | { result: "wrong"; code: IssuedCode; attemptsLeft: number }
  | { result: "locked"; code: IssuedCode; until: number }
  | { result: "expired"; code: IssuedCode };

/** Applies one attempt. A locked or expired code is refused without being checked. */
export function checkCode(
  code: IssuedCode,
  matches: boolean,
  now: number,
  policy: CodePolicy,
): CheckResult {
  if (code.lockedUntil !== undefined && now < code.lockedUntil)
    return { result: "locked", code, until: code.lockedUntil };
  if (code.used || now >= code.expiresAt) return { result: "expired", code };
  if (matches) return { result: "ok", code: { ...code, used: true } };
  const attempts = code.attempts + 1;
  if (attempts >= policy.maxAttempts) {
    const until = now + policy.lockMs;
    return { result: "locked", code: { ...code, attempts, lockedUntil: until }, until };
  }
  return {
    result: "wrong",
    code: { ...code, attempts },
    attemptsLeft: policy.maxAttempts - attempts,
  };
}

/** Seconds until "Send a new code" is offered again. */
export function resendWait(lastSentAt: number, now: number, policy: CodePolicy): number {
  return Math.max(0, Math.ceil((lastSentAt + policy.resendAfterMs - now) / 1_000));
}
